import {
  ITkTableProps,
  TypeTableHead,
  TypeTableBody,
  TypeMultiTableHead,
} from "../../../type/componentsType/TkTable";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import "./_style.scss";
import { useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import { Box } from "@mui/system";
import TkTablePagination from "../TkTablePagination";
import TkHelpIcon from "../TkHelpIcon";
import TkTableContext, {
  TkTableContextProvider,
} from "../../../context/tkTableContext";

/**
 * 一覧表コンポーネント
 * headListとbodyListのpropsは、テーブルコンポーネントのレンダリングに使用されます。
 * @param {Object} props - コンポーネントのprops。
 * @param {Array} props.headList - テーブルのヘッダーセルのリスト。
 * @param {Array} props.bodyList - テーブルのボディセルのリスト。
 * @param {number} [maxHeight=null] - テーブルの最大の高さ。
 * @param {boolean} [hasBorder=true] - テーブルの境界線を表示するかどうか。
 * @param {boolean} [stickyHeader=false] - テーブルのヘッダーを固定するかどうか。
 * @param {string} [noDataMessage="データがありません。"] - データがない場合に表示されるメッセージ。
 * @param {number} [defaultRowsPerPage] - デフォルトのページごとの行数。
 * @param {boolean} [pagin=false] - ページングを有効にするかどうか。
 * @param {number} count - データの総数。
 * @param {Function} onPageChange - ページが変更されたときのコールバック関数。
 * @param {Function} onGetPaginList - ページングリストを取得するときのコールバック関数。
 * @param {React.ReactNode} [headerLeftComponent] - ヘッダーの左側のコンポーネント。
 * @param {boolean} [showUnit=false] - 単位を表示するかどうか。
 * @param {string} [unitText="金額単位(円)"] - 単位のテキスト。
 * @return {React.FC<ITkTableProps>} 一覧表コンポーネント
 */
export const TkTable: React.FC<ITkTableProps> = (props) => {
  return (
    <TkTableContextProvider>
      <TkTableRef {...props} />
    </TkTableContextProvider>
  );
};

export const TkTableRef: React.FC<ITkTableProps> = (props) => {
  const {
    headList,
    bodyList,
    maxHeight = null,
    hasBorder = true,
    stickyHeader = false,
    noDataMessage = "データがありません。",
    // ページング
    defaultRowsPerPage,
    pagin = false,
    count,
    onPageChange,
    onGetPaginList,
    // ページングend
    headerLeftComponent,
    headerRightComponent,
    showUnit = false,
    unitText = "金額単位(円)",
  } = props;
  const [isMultiHead, setIsMultiHead] = useState(false);
  const [showBody, setShowBody] = useState(bodyList);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const { tableContext } = useContext(TkTableContext);

  const pageCont = useMemo(() => {
    return bodyList?.length ? bodyList.length : count;
  }, [count, bodyList]);

  useEffect(() => {
    const validRowsPerPage = tableContext.rowsPerPageInContext;
    setPage(tableContext.pageInContext ?? 0);
    setRowsPerPage(validRowsPerPage as 5 | 10 | 25 | undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableContext.pageInContext, tableContext.rowsPerPageInContext]);

  useEffect(() => {
    headList.forEach((item: TypeMultiTableHead) => {
      if ("top" in item) {
        setIsMultiHead(true);
      }
    });
  }, [headList]);

  useEffect(() => {
    if (!pagin) {
      setShowBody(bodyList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyList, pagin]);

  const handleOnGetPaginList = (paginList: any) => {
    setShowBody(paginList);
    onGetPaginList && onGetPaginList(paginList);
  };

  const TkTableCell = (props: any) => {
    /**
     * テーブルセルのプロパティを定義します。
     * @typedef {Object} TableCellProps
     * @property {string} headTitle - テーブルセルのタイトル。
     * @property {string} [align="center"] - テーブルセルのコンテンツの配置。
     * @property {string} [className] - テーブルセルのCSSクラス名。
     */
    const {
      headTitle,
      helpText,
      align = "center",
      verticalAlign,
      className,
      width,
    } = props;
    return (
      <TableCell
        style={{
          verticalAlign: verticalAlign,
        }}
        width={width ? `${width}` : ""}
        className={`tableCell ${className}`}
        align={align}
        rowSpan={props.rowSpan || 1}
        colSpan={props.colSpan || 1}
      >
        {headTitle}
        {helpText && <TkHelpIcon helpMessage={helpText} />}
      </TableCell>
    );
  };

  return (
    <Box className="tkTableMain">
      <Box className="tkTableHeader">
        <Box className="tkTableHeaderLeft">{headerLeftComponent}</Box>

        <Box className="tkTableHeaderPagin">
          {pagin && (
            <TkTablePagination
              count={pageCont}
              paginList={bodyList}
              onPageChange={onPageChange}
              onGetPaginList={handleOnGetPaginList}
              defaultRowsPerPage={rowsPerPage}
              pageInProps={page}
            />
          )}
        </Box>

        <Box className="tkTableHeaderRight">
          {headerRightComponent}
          {showUnit && <Typography>{unitText}</Typography>}
        </Box>
      </Box>
      <TableContainer
        data-testid="tkTableForTest"
        className="tkTableContainer"
        style={
          {
            "--maxHeight": maxHeight ? `${maxHeight}px` : "",
          } as React.CSSProperties
        }
      >
        <Table
          className="tkTable"
          style={
            {
              "--border": hasBorder ? "1px solid rgba(0, 0, 0, 1)" : "none",
              "--borderBottom": hasBorder
                ? "1px solid rgba(0, 0, 0, 1)"
                : "1px solid rgb(128, 128, 128)",
            } as React.CSSProperties
          }
        >
          <TableHead
            className={stickyHeader ? "sticky tkTableHead" : "tkTableHead"}
          >
            {isMultiHead ? (
              <>
                <TableRow>
                  {headList?.map((item: TypeMultiTableHead, index: number) => {
                    return (
                      <React.Fragment key={`top-${index}`}>
                        {item.headTitle ? (
                          <TkTableCell {...item} rowSpan={2} />
                        ) : (
                          <>
                            {item.top && (
                              <TkTableCell
                                {...item.top}
                                colSpan={item.bottom ? item.bottom.length : 1}
                              />
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
                <TableRow>
                  {headList?.map((item: TypeMultiTableHead, index: number) => {
                    return (
                      <React.Fragment key={`bottom-${index}`}>
                        {item.bottom &&
                          item.bottom.map(
                            (bottomItem: TypeTableHead, index: number) => {
                              return (
                                <React.Fragment key={`bottom-index-${index}`}>
                                  <TkTableCell {...bottomItem} />
                                </React.Fragment>
                              );
                            }
                          )}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              </>
            ) : (
              <TableRow>
                {headList?.map((item: TypeTableHead, index: number) => {
                  return (
                    <React.Fragment key={index}>
                      <TkTableCell {...item} />
                    </React.Fragment>
                  );
                })}
              </TableRow>
            )}
          </TableHead>

          <TableBody className="tkTableBody">
            {showBody?.length ? (
              showBody?.map((bodyItem: TypeTableBody[], index: number) => {
                return (
                  <TableRow className="tableRow" key={index}>
                    {bodyItem.map((item: TypeTableBody, index: number) => {
                      if (item.id) return null;
                      /**
                       * TkTableコンポーネントは、表形式のデータを表示するために使用されます。
                       *
                       * @param children - TkTable内に表示されるReact要素。
                       * @param align - TkTable内のセルのテキストの水平方向の配置。"left"、"center"、または"right"のいずれかを指定できます。
                       * @param className - TkTableに適用されるCSSクラス名。
                       * @param cellWidth - TkTable内の各セルの幅を指定するための数値。headListの長さに基づいて計算されます。
                       */
                      const {
                        children,
                        align,
                        verticalAlign,
                        className,
                        width,
                        backgroundColor,
                      } = item;

                      return (
                        <TableCell
                          style={{
                            verticalAlign: verticalAlign,
                            wordWrap: "break-word",
                            wordBreak: "break-all",
                            backgroundColor: backgroundColor,
                          }}
                          width={width ? `${width}` : ""}
                          className={`tableCell ${className}`}
                          align={align}
                          key={index}
                          data-testid="tkTableBodyCellForTest"
                        >
                          {children}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  className="tableCell"
                  align="center"
                  colSpan={headList?.length}
                >
                  {noDataMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="tkTableHeader">
        <Box className="tkTableHeaderLeft"></Box>

        <Box className="tkTableHeaderPagin">
          {pagin && (
            <TkTablePagination
              count={pageCont}
              paginList={bodyList}
              onPageChange={onPageChange}
              onGetPaginList={handleOnGetPaginList}
              defaultRowsPerPage={rowsPerPage}
              pageInProps={page}
            />
          )}
        </Box>

        <Box className="tkTableHeaderRight"></Box>
      </Box>
    </Box>
  );
};

export default TkTable;
